import { Button, Fab } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import FakeSearchInput from '@/components/modules/mobile/FakeSearchInput';
import { ROUTE_BUILDER } from '@/constants/pages';
import { classnames } from '@/helpers';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { Position, positionSchema } from '@/hooks/search/useSearch.helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { autocompleteService } from '@/services';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import KeywordAutoSuggest from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest';
import useKeywordAutoSuggestManager from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import LocationAutosuggest from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest';
import useLocationAutoSuggestManager from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import { LocationSuggestion } from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.types';
import {
  AutoSuggestInputComponent,
  KeywordAutoSuggestSectionTitle,
  KeywordSuggestionItem,
  LocationAutoSuggestSectionTitle,
  LocationSuggestionItem,
} from '../../components/AutoSuggest/shared/templates';
import { AutoSuggestInputTheme } from '../../components/AutoSuggest/shared/types';
import { SEARCH_CONTEXT } from '../../constants/tracking';
import { SearchContext } from '../../types/tracking';

import { LOCATIONS_SEARCH_EXPERIMENT } from '@/constants/experimentConstants';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { clearLastLocationSuggestionFromLocalStorage } from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';

const baseTranslationKey = 'features.searchV2.modules.FakeSearchForm';

const AUTOGUSGEST_THEME: AutoSuggestInputTheme = {
  container: 'relative w-full',
  suggestionsContainer: 'absolute w-full right-0 left-0 max-h-[550px]',
  suggestion: 'cursor-pointer hover:bg-surface_l4 rounded-md space-x-md',
  suggestionHighlighted: 'bg-surface_l4',
  suggestionsList: 'space-y-xs',
  suggestionsContainerOpen:
    'z-[1001] bg-white border border-border_bold bg-white p-xl mt-xs rounded-md max-h-[550px] overflow-y-auto',
};

type FakeSearchFormProps = {
  isSistaminuten?: boolean;
  locationSource?: string;
};

const FakeSearchFormDesktop = (props: FakeSearchFormProps) => {
  const [locationId, setLocationId] = useState<string>(undefined);
  const [position, setPosition] = useState<Position>(undefined);
  const [searchContext, setSearchContext] = useState<SearchContext>('home_page_free_search');
  const [isFocused, setIsFocused] = useState<'keyword' | 'location'>();

  const history = useHistory();
  const location = useLocation();
  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: '',
      suggestions: [],
      newServices: [],
      showSuggestions: true,
    },
    isSistaminutenSearch: props.isSistaminuten,
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: '',
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    onSelectLocationSuggestion: handleOnUpdateLocation,
  });
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);

  function handleOnUpdateLocation(params) {
    // reset previous state
    setLocationId(undefined);
    setPosition(undefined);

    if (params.locationId) {
      setLocationId(params.locationId);
    }

    const position = `${params['lat']}-${params['lon']}`;

    if (positionSchema.safeParse(position).success) {
      setPosition(position);
    }
  }
  function handleOnSelectKeywordSuggestion() {
    setSearchContext('home_page_search_autocomplete');
    /**
     * Make autosuggest keyword trigger auto focus on location input if empty
     */
    if (!locationAutoSuggestManager.location) {
      locationInputRef?.current?.focus?.();
    }
  }

  function handleOnClearKeyword() {
    keywordInputRef?.current?.focus?.();
    keywordAutoSuggestManager.onChange(null, { newValue: '' });
  }

  function handleClearLocation() {
    locationInputRef?.current?.focus?.();
    setLocationId(undefined);
    setPosition(undefined);
    locationAutoSuggestManager.onChange({ target: { value: '' } });
    setLocationId(undefined);
    setPosition(undefined);
  }

  function handleOnSearchClick() {
    const state = {
      keyword: keywordAutoSuggestManager.keyword || DEFAULT_SEARCH_KEYWORD,
      location: locationAutoSuggestManager.location || DEFAULT_SEARCH_LOCATION,
    };

    const query = new URLSearchParams(location.search);

    if (position && state.location === DEFAULT_MY_LOCATION_KEYWORD) {
      const [lat, lon] = position.split('-');
      query.set('lat', lat);
      query.set('lon', lon);
    }

    if (props?.locationSource) {
      query.set('locationSource', props.locationSource);
    }

    let pathname = ROUTE_BUILDER.serp(state.keyword, state.location);

    if (props.isSistaminuten) {
      pathname = ROUTE_BUILDER.sistaMinutenSerp(state.keyword, state.location);
    }

    history.push({ pathname, search: query.toString(), state: { searchContext } });
  }

  function handleRemoveLocationHistorySuggestion(suggestion: LocationSuggestion) {
    autocompleteService.deleteLocationSuggestion(suggestion.label);
    locationAutoSuggestManager.onClearHistorySuggestion(suggestion);
    clearLastLocationSuggestionFromLocalStorage();
  }

  function handleOnAutoSuggestKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      handleOnSearchClick();
    }
  }

  function handleOnFocus(type: 'keyword' | 'location', cb: () => void) {
    setIsFocused(type);
    cb?.();
  }

  function handleOnBlur(cb: () => void) {
    setIsFocused(undefined);
    cb?.();
  }

  /**
   * If both keyword and location are selected, trigger search
   */
  useEffect(() => {
    if (
      locationAutoSuggestManager.location &&
      keywordAutoSuggestManager.keyword &&
      searchContext === 'home_page_search_autocomplete' &&
      isFocused === undefined
    ) {
      handleOnSearchClick();
    }
  }, [locationAutoSuggestManager.location, keywordAutoSuggestManager.keyword]);

  const inputSharedClassNames = classnames(
    'border-border_regular group-hover:border-border_bold group-hover:bg-surface_l4 h-[52px]',
    isFocused ? 'bg-surface_l4' : 'bg-surface_l2',
  );

  const hasKeywordInput = Boolean(keywordAutoSuggestManager.keyword);
  const hasLocationInput = Boolean(locationAutoSuggestManager.location);
  const hasDirtyFields = Boolean(hasKeywordInput || hasLocationInput);

  return (
    <div className="group flex items-stretch">
      <div className="grid w-full grid-cols-2">
        <div className="relative">
          <KeywordAutoSuggest
            inputRef={keywordInputRef}
            {...keywordAutoSuggestManager}
            onKeyDown={handleOnAutoSuggestKeyDown}
            theme={AUTOGUSGEST_THEME}
            renderInputComponent={({ key, ...inputProps }) => (
              <AutoSuggestInputComponent
                key={key}
                inputProps={{
                  ...inputProps,
                  onFocus: () => handleOnFocus('keyword', inputProps.onFocus),
                  onBlur: () => handleOnBlur(inputProps.onBlur),
                  /*
                   * Change placeholder with title when input is focused
                   */
                  placeholder: !isFocused ? inputProps.title : inputProps.placeholder,
                }}
                isFocused={isFocused === 'keyword'}
                onClear={handleOnClearKeyword}
                leftSlot={<Icon variant="search" size="sm" />}
                className={classnames(inputSharedClassNames, 'rounded-bl-full rounded-tl-full border-r-0')}
              />
            )}
            renderSuggestion={(suggestion, options) => (
              <KeywordSuggestionItem suggestion={suggestion} options={options} />
            )}
            renderSectionTitle={(section) => <KeywordAutoSuggestSectionTitle {...section} />}
          />
          {/* Seperator */}
          {!isFocused && <div className="bg-border_regular bottom-xs top-xs absolute right-0 w-[1px]"></div>}
        </div>
        <LocationAutosuggest
          inputRef={locationInputRef}
          {...locationAutoSuggestManager}
          onKeyDown={(event) => handleOnAutoSuggestKeyDown(event)}
          theme={AUTOGUSGEST_THEME}
          renderInputComponent={({ key, ...inputProps }) => (
            <AutoSuggestInputComponent
              key={key}
              inputProps={{
                ...inputProps,
                placeholder: isFocused === 'location' ? inputProps.placeholder : _s('where'),
                onFocus: () => handleOnFocus('location', inputProps.onFocus),
                onBlur: () => handleOnBlur(inputProps.onBlur),
              }}
              isFocused={isFocused === 'location'}
              onClear={handleClearLocation}
              leftSlot={
                locationAutoSuggestManager.isFetchingUserPosition ? (
                  <LoadingIcon />
                ) : (
                  <Icon variant="location" size="sm" />
                )
              }
              className={classnames(inputSharedClassNames, 'border-l-0 border-r-0')}
            />
          )}
          renderSuggestion={(suggestion, options) => (
            <LocationSuggestionItem
              suggestion={suggestion}
              options={options}
              onRemoveHistorySuggestion={handleRemoveLocationHistorySuggestion}
            />
          )}
          renderSectionTitle={(section) => <LocationAutoSuggestSectionTitle {...section} />}
        />
      </div>

      <div
        className={classnames(
          inputSharedClassNames,
          'px-xs flex h-auto items-center justify-center rounded-br-full rounded-tr-full border border-l-0',
        )}>
        {hasDirtyFields && (
          <Button
            className="rounded-[1000px]"
            leftIcon={<Icon variant="search" size="xs" />}
            variant="primary"
            size="sm"
            onClick={handleOnSearchClick}>
            {_s('search')}
          </Button>
        )}
        {!hasDirtyFields && (
          <Fab
            onClick={handleOnSearchClick}
            size="sm"
            variant="primary"
            style={{ width: 40, height: 40 }}
            icon={
              <Icon
                variant="search"
                size="xs"
                style={{ width: 16, height: 16, filter: 'invert(1)' }}
                aria-label={_s('search')}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

const FakeSearchFormMobile = (props: FakeSearchFormProps) => {
  const history = useHistory();

  let pathname = ROUTE_BUILDER.serp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION);

  if (props.isSistaminuten) {
    pathname = ROUTE_BUILDER.sistaMinutenSerp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION);
  }

  return (
    <form onSubmit={() => history.push({ pathname, state: { searchContext: SEARCH_CONTEXT.HOME_PAGE_FREE_SEARCH } })}>
      <FakeSearchInput
        isLoading={false}
        onClick={() => {
          history.push({
            pathname: '/search',
            state: {
              fromLandingPage: true,
              searchContext: SEARCH_CONTEXT.HOME_PAGE_FREE_SEARCH,
              isSistaminutenSearch: props.isSistaminuten,
            },
          });
        }}
        icon="search"
        valid={false}
        onClear={() => null}
        content={_s(`${baseTranslationKey}.keywordInput.placeholder`)}
        size="sm"
      />
    </form>
  );
};

const FakeSearchForm = ({ isSistaminuten = false }: FakeSearchFormProps) => {
  const { isMobileView } = useMobileView();
  const flags = useAppSelector((state) => state.flags);

  const getVariant = useGetAmplitudeExperimentVariant();

  const locationSource = flags[LOCATIONS_SEARCH_EXPERIMENT]?.value ?? getVariant(LOCATIONS_SEARCH_EXPERIMENT)?.value;

  if (isMobileView) return <FakeSearchFormMobile isSistaminuten={isSistaminuten} locationSource={locationSource} />;

  return <FakeSearchFormDesktop isSistaminuten={isSistaminuten} locationSource={locationSource} />;
};

export default FakeSearchForm;
